/** Full-screen mobile modal for the AI command bar chat. */
import { useRef, useEffect } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { X } from 'lucide-react';
import { getInitials } from '@formency/shared';
import { useAuth } from '@/shared/auth/AuthProvider';
import { ChatMessageText } from '@/shared/components/ChatMessageText';
import { CommandBar } from './CommandBar';
import { CommandBarActions } from './CommandBarActions';
import {
  LogoAvatar,
  AssistantStreamingBody,
  SessionActionCard,
  JobStartedCard,
  SuggestionPills,
  stripModePrefix,
} from './CommandBarParts';
import type { ActiveToolCall, ChatMessage, PendingSession, PendingJob } from './useCommandBarChat';

interface CommandBarModalProps {
  open: boolean;
  messages: ChatMessage[];
  isStreaming: boolean;
  pendingSession: PendingSession | null;
  pendingJob: PendingJob | null;
  suggestions: string[];
  activeToolCalls: ActiveToolCall[];
  placeholders: string[];
  selectedMode: 'create' | 'revise' | null;
  staticPlaceholder: string | null;
  onSubmit: (text: string, slashCommand: 'create' | 'revise' | null) => void;
  onDismiss: () => void;
  onStartSession: () => void;
  onSendSuggestion: (text: string) => void;
  onSelectAction: (mode: 'create' | 'revise' | 'search') => void;
}

export function CommandBarModal({
  open,
  messages,
  isStreaming,
  pendingSession,
  pendingJob,
  suggestions,
  activeToolCalls,
  placeholders,
  selectedMode,
  staticPlaceholder,
  onSubmit,
  onDismiss,
  onStartSession,
  onSendSuggestion,
  onSelectAction,
}: CommandBarModalProps) {
  const { user } = useAuth();
  const userInitial = getInitials({
    name: user?.name ?? null,
    email: user?.email ?? null,
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const hasMessages = messages.length > 0;
  const showActions = !hasMessages && !isStreaming;

  // The streaming assistant message is the last one in the original array
  const lastAssistantId =
    messages.length > 0 && messages[messages.length - 1].role === 'assistant'
      ? messages[messages.length - 1].id
      : null;

  // Auto-scroll to bottom when messages change or streaming updates
  const lastMessageContent = messages[messages.length - 1]?.content;
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, lastMessageContent]);

  // Focus input when modal opens
  useEffect(() => {
    if (open) {
      const id = setTimeout(() => inputRef.current?.focus(), 300);
      return () => clearTimeout(id);
    }
  }, [open]);

  // Escape key dismisses
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onDismiss();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onDismiss]);

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 z-40 learner-single:hidden"
            style={{ background: 'rgba(0,0,0,0.3)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onDismiss}
          />

          {/* Modal panel */}
          <motion.div
            className="fixed inset-0 z-50 flex flex-col learner-single:hidden"
            style={{
              background: 'var(--color-bg)',
              height: '100dvh',
            }}
            initial={{ opacity: 0, y: '100%' }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: '30%' }}
            transition={{
              type: 'spring',
              stiffness: 400,
              damping: 30,
            }}
          >
            {/* ── Header ─────────────────────────────────────────────────── */}
            <div
              className="flex shrink-0 items-center justify-between px-4"
              style={{
                height: 56,
                borderBottom: '1px solid var(--color-border)',
              }}
            >
              <p className="font-semibold" style={{ fontSize: 16, color: 'var(--color-text)' }}>
                AI Assistant
              </p>
              <button
                type="button"
                onClick={onDismiss}
                className="flex h-8 w-8 items-center justify-center rounded-full transition-colors"
                style={{
                  background: 'var(--color-surface-muted)',
                  color: 'var(--color-text-muted)',
                }}
                aria-label="Close"
              >
                <X size={18} strokeWidth={2} />
              </button>
            </div>

            {/* ── Message area / Actions ──────────────────────────────────── */}
            <div
              ref={scrollRef}
              className="flex min-h-0 flex-1 flex-col overflow-y-auto"
              style={{ scrollbarWidth: 'none' }}
            >
              {showActions ? (
                <div className="flex flex-1 flex-col items-center justify-center px-4 py-6">
                  <CommandBarActions onSelect={onSelectAction} />
                </div>
              ) : (
                <div className="flex flex-1 flex-col justify-end px-4 py-4">
                  {/* Messages — oldest at top, newest at bottom */}
                  <div className="flex flex-col gap-3">
                    <AnimatePresence mode="popLayout">
                      {messages.map((msg) => (
                        <motion.div
                          key={msg.id}
                          initial={{ opacity: 0, y: 10, scale: 0.97 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          transition={{ duration: 0.3, ease: 'easeOut' }}
                          className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          {msg.role === 'assistant' && (
                            <div className="flex max-w-[85%] gap-2.5">
                              <LogoAvatar />
                              <div
                                className="rounded-2xl rounded-tl-md px-4 py-2.5"
                                style={{
                                  background: 'var(--color-surface-solid)',
                                  border: '1px solid var(--color-border)',
                                }}
                              >
                                <div
                                  className="leading-relaxed"
                                  style={{
                                    fontSize: 'var(--text-sm)',
                                    color: 'var(--color-text-secondary)',
                                  }}
                                >
                                  {msg.id === lastAssistantId && isStreaming ? (
                                    <AssistantStreamingBody
                                      text={msg.content}
                                      activeToolCalls={activeToolCalls}
                                    />
                                  ) : (
                                    <ChatMessageText text={msg.content} />
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                          {msg.role === 'user' && (
                            <div className="flex max-w-[75%] flex-row-reverse gap-2.5">
                              <div
                                className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                                style={{ background: 'var(--color-accent)' }}
                              >
                                <span
                                  className="select-none font-semibold"
                                  style={{ fontSize: '10px', color: 'var(--color-text-on-accent)' }}
                                >
                                  {userInitial}
                                </span>
                              </div>
                              <div
                                className="rounded-2xl rounded-tr-md px-4 py-2.5"
                                style={{ background: 'var(--color-accent-subtle)' }}
                              >
                                <p
                                  className="font-medium"
                                  style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}
                                >
                                  {stripModePrefix(msg.content)}
                                </p>
                              </div>
                            </div>
                          )}
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>

                  {/* Action cards + suggestion pills below newest message */}
                  <AnimatePresence>
                    {pendingSession && !isStreaming && (
                      <div className="mt-3">
                        <SessionActionCard onStart={onStartSession} />
                      </div>
                    )}
                    {pendingJob && (
                      <div className="mt-3">
                        <JobStartedCard />
                      </div>
                    )}
                  </AnimatePresence>
                  <AnimatePresence>
                    {suggestions.length > 0 && !isStreaming && !pendingJob && (
                      <div className="mt-3">
                        <SuggestionPills items={suggestions} onSelect={onSendSuggestion} />
                      </div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>

            {/* ── Bottom input ────────────────────────────────────────────── */}
            <div
              className="shrink-0 px-4 pt-2"
              style={{
                borderTop: '1px solid var(--color-border)',
                paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
                background: 'var(--color-bg)',
              }}
              onMouseDown={(e) => {
                if (e.target !== inputRef.current) {
                  e.preventDefault();
                }
              }}
            >
              <CommandBar
                inputRef={inputRef}
                placeholders={placeholders}
                expanded={true}
                disabled={isStreaming}
                hasMessages={hasMessages}
                externalSlashCommand={selectedMode}
                alwaysShowSend
                staticPlaceholder={pendingJob ? 'Your lesson is on its way...' : staticPlaceholder}
                onSubmit={onSubmit}
                onDismiss={onDismiss}
                onFocus={() => {}}
                onBlur={() => {}}
              />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
